import { useEffect, useRef } from 'react';
import { ExtendedNestedSubAccount } from '../OrgChartContainer';
import { Box, Card, CircleButton, formattedCurrency } from 'vopay';
import { Button, Title } from '@/components/UI';
import { useForm } from '@/components/Form/utils';
import {
  AccountTransferRequest,
  AccountTransferSerializedRequest,
  useAccountTransferRows,
} from './useAccountTransferRows.helper';
import { useClientData } from '@/providers/ClientData.provider';
import usePostAccountBalance from '@/hooks/useApi/usePostAccountBalance';
import useDataFetchIndicator from '@/hooks/useDataFetchIndicator';
import useSubAccounts from '@/hooks/useApi/useSubAccounts';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useFetchSubAccounts from '@/hooks/useApi/useFetchSubAccounts';
import useFetchPortalSubAccounts from '@/hooks/useApi/useFetchPortalSubAccounts';

const SUB_ACCOUNTS_LIMIT = 50;

interface AccountTransferProps {
  node: ExtendedNestedSubAccount;
  onClose: () => void;
}

const AccountTransfer: React.FC<AccountTransferProps> = ({ node, onClose }) => {
  const {
    response: { AccountID, AccountName, LoginUserName, Success, Country, clientAccountsEnabled },
  } = useClientData();

  const alertDialog = useAlertDialog();
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const [accountBalanceResponse, getAccountBalance] = usePostAccountBalance({});
  const { transferAccountTo } = useSubAccounts();
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.List.OrgChart.AccountTransfer' });
  // Heavy endpoint kept only to resolve the picked account's balance on select.
  const [, , , fetchFromSubAccountsAsync] = useFetchSubAccounts({});
  const [, , , fetchToSubAccountsAsync] = useFetchSubAccounts({});
  // Lightweight, searchable list that backs the select dropdowns.
  const fromPortalSubAccounts = useFetchPortalSubAccounts();
  const toPortalSubAccounts = useFetchPortalSubAccounts();

  useEffect(() => {
    const loadInitial = async () => {
      try {
        await Promise.all([
          fromPortalSubAccounts.fetchPortalSubAccounts({ Limit: SUB_ACCOUNTS_LIMIT }),
          toPortalSubAccounts.fetchPortalSubAccounts({ Limit: SUB_ACCOUNTS_LIMIT }),
        ]);
      } catch (error) {
        console.error(error);
      }
    };

    void loadInitial();
  }, []);

  const onSearchFromSubAccounts = (searchText: string) => {
    fromPortalSubAccounts.fetchPortalSubAccounts({ SearchText: searchText, Limit: SUB_ACCOUNTS_LIMIT });
  };

  const onSearchToSubAccounts = (searchText: string) => {
    toPortalSubAccounts.fetchPortalSubAccounts({ SearchText: searchText, Limit: SUB_ACCOUNTS_LIMIT });
  };

  const initialFromLabel = `${node.AccountName} ($${formattedCurrency(node.AvailableBalance || '0.00')})`;

  const setFieldValueRef = useRef<
    ((field: string, value: string | undefined, shouldValidate?: boolean) => void) | null
  >(null);

  const fromTokenRef = useRef<string | undefined>(undefined);

  const { fieldDataList, isFromResolved } = useAccountTransferRows({
    MainAccountName: AccountName,
    MainAccountId: AccountID,
    MainAccountBalance: accountBalanceResponse?.AvailableFunds || '0.00',
    MainClientAccountsEnabled: clientAccountsEnabled,
    fromSubAccounts: fromPortalSubAccounts.subAccounts,
    toSubAccounts: toPortalSubAccounts.subAccounts,
    fromFetching: fromPortalSubAccounts.fetching,
    toFetching: toPortalSubAccounts.fetching,
    country: Country,
    initialFromLabel,
    initialFromAccountId: node.AccountID,
    initialCurrency: node.Currency,
    setFieldValueRef,
    fromTokenRef,
    onFromResolveError: () =>
      alertDialog.error(
        t('ErrorMessages.transferError'),
        t('ErrorMessages.resolveBalanceError', {
          defaultValue: 'Failed to resolve the available balance for the selected "From" account',
        }),
      ),
    onSearchFromSubAccounts,
    onSearchToSubAccounts,
    fetchFromSubAccountsAsync,
    fetchToSubAccountsAsync,
  });

  const history = useHistory();

  const { renderFormElements, ctx } = useForm<AccountTransferRequest, AccountTransferSerializedRequest>({
    config: fieldDataList,
    initialValues: {
      Amount: '',
      RecipientAccountID: '',
      DebitorAccountID: node.AccountID,
      Currency: node.Currency,
    },
    onSubmit: async (serializedData) => {
      try {
        setDataFetchingInfo(true);

        // NOTE: Faizal says we should not use /account/transfer-from anymore.
        // Workaround for transfer-from: run a single transfer-to authenticated as
        // the "From" account via its own JWT.
        const fromMain = AccountID === serializedData.DebitorAccountID;
        const bearer = fromMain ? undefined : fromTokenRef.current;

        // Without a bearer the call would silently run as the logged-in parent
        // and debit the wrong account — never let that reach the API.
        if (!fromMain && !bearer) {
          throw new Error(
            t('ErrorMessages.missingFromToken', {
              defaultValue: 'The selected "From" account could not be verified. Please re-select it and try again.',
            }),
          );
        }

        const resp = await transferAccountTo(
          {
            Amount: serializedData.Amount,
            RecipientAccountID: serializedData.RecipientAccountID,
            Currency: serializedData.Currency,
          },
          bearer,
        );

        history.push({
          pathname: `/transactions-history/details/AccountTransfer/${resp.TransactionID}/${serializedData.Currency}/`,
          state: {
            transactionId: resp.TransactionID || '',
            activeModule: 'accountTransfer',
            hasNewTransactionButton: true,
          },
        });

        onClose();
      } catch (err) {
        const msg = err instanceof Error ? err.message : t('ErrorMessages.defaultMessage');
        console.error('Account Transfer Error', msg);
        alertDialog.error(t('ErrorMessages.transferError'), msg);
      } finally {
        setDataFetchingInfo(false);
      }
    },
  });

  setFieldValueRef.current = ctx.setFieldValue;

  useEffect(() => {
    if (Success) {
      getAccountBalance({
        PortalUser: LoginUserName,
        Currency: ctx.values.Currency,
      });
    }
  }, [ctx.values.Currency]);

  return (
    <Card
      data-testid="accountTransfer-Card"
      hoverEffect={false}
      style={{ padding: '32px', backgroundColor: 'var(--vopayWhite)' }}
    >
      <form
        style={{
          minWidth: '600px',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
        }}
        onSubmit={ctx.handleSubmit}
      >
        <Box flx="col" gapSize="15px">
          <Box jc="fe">
            <CircleButton onClick={onClose} icon="close" />
          </Box>

          <Title level="3">{t('title')}</Title>
        </Box>

        {renderFormElements({
          config: fieldDataList,
        })}

        <Button type="submit" colorScheme="primary" disabled={!ctx.isValid || !isFromResolved}>
          {t('submitButton')}
        </Button>
      </form>
    </Card>
  );
};

export default AccountTransfer;
